import React, { useState, useEffect } from 'react'
import { autoService } from '@services/autoService'
import { processAssetData } from '@utils/assethelper'
import { VARIANT_ID } from '@components/Product/ProductWidget/constants'
import Image from 'next/image'
import { defaultVehicleImage } from '@components/MainWidget/ChallanWidgetModals/constants'
import BankListShimmer from '@components/MainWidget/FastagWidget/bankListShimmer'
import { ModalType } from '@enums/ModalType'
import { segmentEventCommonPropFunc } from '@utils/segmentProperties'
import { pageEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { waitForAnalyticsLoad } from '@components/utils/analyticsViewPage'
import { SEGMENT } from '@components/MainWidget/common/widgetSegments'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import { getCommonWAChallanProperties } from '@factories/getWhatsAppChallanProperties'
import { getClassNames } from '@helpers/productWidgetHelper'
import { useEnvVariablesClientConfig } from '@context/EnvClientContext/EnvVariablesClientContext'
import AppRedirectComp from './AppRedirect'
import ChallanList from './ChallanList'
import QuickScan from './QuickScan'
import { getRefactoredChallans, getRegNumberInfo } from './helper'
import styles from './WhatsappChallanNewFlow.module.scss'
import { FireEventProps, IWhatsappChallanNewFlow, SimplifiedChallan } from './interfaces'
import { fetchUrlBase, trafficLoaderText, trafficReportText } from './constants'
import { WHATSAPP_CHALLAN_PAGE_TYPE } from './enums'

const WhatsappChallanNewFlow = ({
  regNum,
  phoneNumber,
  utmLead,
  segmentEventCommonProperties,
  widgetType,
}: IWhatsappChallanNewFlow) => {
  const { stateName, isNonVahanState, isNationalVehicle } = getRegNumberInfo(regNum)
  const [challans, setChallans] = useState<SimplifiedChallan[] | null>(null)
  const [vehicleData, setVehicleData] = useState<Record<string, any> | null>(null)
  const [vehicleImageData, setVehicleImageData] = useState<Record<string, any> | null>(null)
  const [showQuickScan, setshowQuickScan] = useState(true)
  const envConfig = useEnvVariablesClientConfig()

  useEffect(() => {
    void (async () => {
      try {
        const vehicleResponse = await autoService(regNum)
        setVehicleData(vehicleResponse || [])

        if (vehicleResponse?.[VARIANT_ID]) {
          const vehicleImageResponse = await processAssetData(vehicleResponse, fetchUrlBase)
          setVehicleImageData(vehicleImageResponse?.variantDetails || {})
        } else {
          setVehicleImageData({})
        }
      } catch (error) {
        console.error('Error fetching vehicle data or image:', error)
      }
    })()
  }, [])

  useEffect(() => {
    if (envConfig?.ACKO_ENV) {
      void (async () => {
        const resolvedFlags = await getRefactoredChallans(regNum, envConfig.ACKO_ENV || 'prod')
        setChallans(resolvedFlags)
      })()
    }
  }, [envConfig])

  const fireEvent = async ({
    pageName,
    journey = undefined,
    ctaText = undefined,
    vasAmount = undefined,
  }: FireEventProps) => {
    const properties = await segmentEventCommonPropFunc(segmentEventCommonProperties)
    const commonProperties = getCommonWAChallanProperties(utmLead, phoneNumber, regNum, vehicleData)
    void waitForAnalyticsLoad().then(() => {
      pageEvent(SEGMENT.VIEW_PAGE, {
        ...properties,
        ...commonProperties,
        page: pageName,
        journey,
        cta_text: ctaText,
        vas_amount: vasAmount,
      }).catch(err => {
        console.error('Wait for analytics load error', err)
      })
    })
  }

  useEffect(() => {
    void fireEvent({ pageName: WHATSAPP_CHALLAN_PAGE_TYPE.SCANNING_LOADER })
  }, [])

  return (
    <div className={styles.wrapperContainer}>
      <div className={styles.whatsappFlowContainer}>
        <div className={styles.topContainer}>
          <div className={styles.textContainer}>
            <div className={styles.makeRegNo}>
              <PropertyControlledComponent
                controllerProperty={!!vehicleData}
                fallback={<BankListShimmer shimmerNumber={1} shimmerSize={ModalType.EXTRA_SMALL} />}>
                <>
                  <p>
                    {`${vehicleData?.db_make_name || ''} ${vehicleData?.db_model_name || ''}`.trim() ||
                      vehicleData?.model_name ||
                      '-'}
                  </p>
                  &nbsp;
                </>
              </PropertyControlledComponent>
              <p>{`• ${regNum || ''}`}</p>
            </div>

            <PropertyControlledComponent
              controllerProperty={isNonVahanState || (!isNonVahanState && showQuickScan)}
              fallback={<div className={`${styles.heading} ${styles.fadeIn}`}>{trafficReportText}</div>}>
              <div
                className={getClassNames([styles.heading], {
                  [styles.fadeOut]: !isNonVahanState,
                  [styles.lessDelay]: isNationalVehicle,
                })}>
                {trafficLoaderText}
              </div>
            </PropertyControlledComponent>
          </div>
          {vehicleImageData ? (
            <Image
              src={
                vehicleImageData?.image ||
                defaultVehicleImage?.[vehicleData?.vehicle_type_v2] ||
                defaultVehicleImage?.Default
              }
              alt="Vehicle Image"
              className={styles.vehicleImageData}
              width={100}
              height={60}
              loading="lazy"
            />
          ) : (
            <BankListShimmer shimmerNumber={1} shimmerSize={ModalType.MEDIUM_PLUS} />
          )}
        </div>
        <div className={styles.bottomContainer}>
          <PropertyControlledComponent
            controllerProperty={showQuickScan}
            fallback={
              <>
                <ChallanList challans={challans} isNonVahanState={isNonVahanState} />
                <AppRedirectComp
                  utmLead={utmLead}
                  regNum={regNum}
                  phoneNo={phoneNumber}
                  widgetType={widgetType}
                  numOfChallans={challans?.length}
                  stateName={stateName}
                  isNonVahanState={isNonVahanState}
                  vehicleData={vehicleData}
                />
              </>
            }>
            <QuickScan
              setshowQuickScan={setshowQuickScan}
              state={stateName}
              isNonVahanState={isNonVahanState}
              isNationalVehicle={isNationalVehicle}
              onComplete={fireEvent}
              vasAmount={challans?.length}
            />
          </PropertyControlledComponent>
        </div>
      </div>
    </div>
  )
}

export default WhatsappChallanNewFlow
